import { sortSubroutineFolders, sortSubroutines } from '@/util/sort';
import { useApplication } from '@/hooks/useApplication';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useMetadata } from '@/hooks/useMetadata';
import { Application } from '@/types/application';
import { SearchBlocksItem } from '@/types/search';
import { OpenEditType, Subroutine, SubroutineFolder, SubroutineSortType } from '@/types/subroutine';
import { ValidationError } from '@/types/validation';
import { findSubroutine } from '@/util/application';
import { validateSubroutine } from '@/util/validation/subroutines';
import { getMostSevereLevel } from '@/util/validation/validation';
import { AccountTree, StopTwoTone } from '@mui/icons-material';
import { FC } from 'react';
import StyledTreeItem from './StyledTreeItem';
import TreeLabel from './TreeLabel';
import { SelectedItem } from './TreeMenu';

export type SubroutineTreeItemsWrapperProps = {
  children: React.ReactNode;
  isRoot?: boolean;
  folderId?: number;
  subroutine?: Subroutine;
  deleteFolder?: (folderId: number) => void;
  openEdit: (openEdit: OpenEditType) => void;
  openSearch?: (searchValue: SearchBlocksItem) => void;
  openHandleFoldersDialog: (selectedItem?: SelectedItem) => void;
  handleOnSort?: (sortSubroutine: SubroutineSortType) => void;
  setSelectedTreeMenuItem?: (newId: string) => void;
};

type SubroutineTreeItemsProps = {
  itemKey: string;
  onClick: (subroutineId: number) => void;
  wrapper?: React.FC<SubroutineTreeItemsWrapperProps>;
  validate?: boolean;
  deleteFolder?: (folderId: number) => void;
  openEdit?: (openEdit: OpenEditType) => void;
  openSearch?: (searchValue: SearchBlocksItem) => void;
  openHandleFoldersDialog?: (selectedItem?: SelectedItem) => void;
  sortSubroutinesBy?: SubroutineSortType;
};

const SubroutineTreeItems: FC<SubroutineTreeItemsProps> = (props) => {
  const { application } = useApplication();
  const { errors } = useErrorHandler();
  const { metadata } = useMetadata();

  const handleSearch = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, subroutine: Subroutine) => {
    event.stopPropagation();
    props.openSearch && props.openSearch({ searchQuery: subroutine.label || '', searchType: 'Subroutine' });
  };

  const validateSub = (application: Application, subroutine: Subroutine): ValidationError[] => {
    errors.filter((error) => error.id && subroutine.nodes.includes(error.id));
    const subErrors = validateSubroutine(
      application.blocks,
      application.subroutines,
      subroutine.id,
      application.variables,
      metadata
    );
    const subBlockErrors = errors.filter((error) => error.id && subroutine.nodes.includes(error.id));
    return [...subErrors, ...subBlockErrors];
  };

  const renderSubroutines = (folder?: SubroutineFolder) => {
    return sortSubroutines(
      props.sortSubroutinesBy,
      application.subroutines?.filter((subroutine) => subroutine.folder === folder?.id)
    )?.map((subroutine) => {
      const currentSubroutine = findSubroutine(application.subroutines, subroutine.id);
      if (!currentSubroutine) return;

      const errors = validateSub(application, currentSubroutine);
      const hasErrors = props.validate && !!errors.length;
      const content = (
        <StyledTreeItem
          key={`${props.itemKey}.${subroutine.id}`}
          itemId={`${props.itemKey}.${subroutine.id}`}
          onClick={() => props.onClick(subroutine.id)}
          label={
            <TreeLabel
              icon={StopTwoTone}
              label={subroutine.label}
              IconTitle='Find References'
              onClick={(event) => handleSearch(event, subroutine)}
              {...(hasErrors && {
                tooltip: 'This subroutine contains issues',
                error: getMostSevereLevel(errors)
              })}
            />
          }
        />
      );
      return props.wrapper ? (
        <props.wrapper
          key={`wrapper.${props.itemKey}.${subroutine.id}`}
          deleteFolder={(folderId) => props.deleteFolder && props.deleteFolder(folderId)}
          openEdit={(openEdit) => props.openEdit && props.openEdit(openEdit)}
          openSearch={props.openSearch}
          openHandleFoldersDialog={(selectedItem) =>
            props.openHandleFoldersDialog && props.openHandleFoldersDialog(selectedItem)
          }
          folderId={folder?.id}
          subroutine={currentSubroutine}
        >
          {content}
        </props.wrapper>
      ) : (
        content
      );
    });
  };

  const renderFolder = (folder: SubroutineFolder) => {
    const results = [];
    results.push(
      sortSubroutineFolders(
        props.sortSubroutinesBy,
        application.folders?.filter((innerFolder) => innerFolder.parentId === folder.id)
      )?.map((folder) => {
        const content = (
          <StyledTreeItem
            key={`${props.itemKey}.folder${folder.id}`}
            itemId={`folders.${folder.id}`}
            label={<TreeLabel icon={AccountTree} label={folder.label} />}
          >
            {renderFolder(folder)}
          </StyledTreeItem>
        );
        return props.wrapper ? (
          <props.wrapper
            key={`wrapper.${props.itemKey}.folder${folder.id}`}
            folderId={folder?.id}
            deleteFolder={(folderId) => props.deleteFolder && props.deleteFolder(folderId)}
            openEdit={(openEdit) => props.openEdit && props.openEdit(openEdit)}
            openSearch={props.openSearch}
            openHandleFoldersDialog={(selectedItem) =>
              props.openHandleFoldersDialog && props.openHandleFoldersDialog(selectedItem)
            }
          >
            {content}
          </props.wrapper>
        ) : (
          content
        );
      })
    );
    results.push(renderSubroutines(folder));
    return results;
  };

  const renderFolders = () => {
    const results = [];
    results.push(
      sortSubroutineFolders(
        props.sortSubroutinesBy,
        application.folders?.filter((folder) => !folder.parentId)
      )?.map((folder) => {
        const content = (
          <StyledTreeItem
            key={`${props.itemKey}.folder${folder.id}`}
            itemId={`folders.${folder.id}`}
            label={<TreeLabel icon={AccountTree} label={folder.label} />}
          >
            {renderFolder(folder)}
          </StyledTreeItem>
        );
        return props.wrapper ? (
          <props.wrapper
            key={`wrapper.${props.itemKey}.folder${folder.id}`}
            folderId={folder?.id}
            deleteFolder={(folderId) => props.deleteFolder && props.deleteFolder(folderId)}
            openEdit={(openEdit) => props.openEdit && props.openEdit(openEdit)}
            openSearch={props.openSearch}
            openHandleFoldersDialog={(selectedItem) =>
              props.openHandleFoldersDialog && props.openHandleFoldersDialog(selectedItem)
            }
          >
            {content}
          </props.wrapper>
        ) : (
          content
        );
      })
    );
    results.push(renderSubroutines());
    return results;
  };

  return (
    <StyledTreeItem itemId='subroutines' label={<TreeLabel icon={AccountTree} label='Flow' />}>
      {renderFolders()}
    </StyledTreeItem>
  );
};

export default SubroutineTreeItems;
